import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const LOGO_ICON = 'https://static.prod-images.emergentagent.com/jobs/85ba9bbd-736c-4dbb-ae2d-37b84f64fb5b/images/46c4c99dc82a03ab2b247ccff337db0bc40f8a2e674401b4d8deca221940954b.png';

export default function SetPinScreen() {
  const navigate = useNavigate();
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [step, setStep] = useState('set'); // 'set' | 'confirm' | 'done'
  const [showPin, setShowPin] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePinInput = (index, value, isConfirm = false) => {
    if (value && !/^\d$/.test(value)) return;
    const arr = isConfirm ? [...confirmPin] : [...pin];
    arr[index] = value;
    isConfirm ? setConfirmPin(arr) : setPin(arr);
    if (value && index < 3) {
      document.getElementById(`${isConfirm ? 'confirm' : 'set'}-pin-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (index, e, isConfirm = false) => {
    if (e.key === 'Backspace' && !((isConfirm ? confirmPin : pin)[index]) && index > 0) {
      document.getElementById(`${isConfirm ? 'confirm' : 'set'}-pin-${index - 1}`)?.focus();
    }
  };

  const handleSetPin = () => {
    if (pin.join('').length !== 4) { toast.error('Enter all 4 digits'); return; }
    setStep('confirm');
    setConfirmPin(['', '', '', '']);
    setTimeout(() => document.getElementById('confirm-pin-0')?.focus(), 200);
  };

  const handleConfirm = async () => {
    if (pin.join('') !== confirmPin.join('')) {
      toast.error('PINs do not match. Try again.');
      setConfirmPin(['', '', '', '']);
      setTimeout(() => document.getElementById('confirm-pin-0')?.focus(), 200);
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/set-pin', { pin: pin.join('') });
      toast.success('PIN set successfully!');
      setStep('done');
    } catch {
      toast.error('Failed to set PIN. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen font-[Nunito] flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #FFFBF5 0%, #FFF7ED 50%, #FEF3C7 100%)' }}>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <img src={LOGO_ICON} alt="Yukti" className="w-10 h-10" />
            <span className="text-2xl font-bold text-[#1F2937] font-[Outfit]">Yukti</span>
          </div>
          <p className="text-sm text-[#9CA3AF]">Secure your parent dashboard</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-orange-100 overflow-hidden" data-testid="set-pin-card">
          <div className="bg-gradient-to-r from-[#C2410C] to-[#EA580C] px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                {step === 'done' ? <ShieldCheck className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h2 className="text-white font-bold text-lg font-[Outfit]">
                  {step === 'set' && 'Create Your PIN'}
                  {step === 'confirm' && 'Confirm Your PIN'}
                  {step === 'done' && 'PIN Set Successfully!'}
                </h2>
                <p className="text-white/70 text-xs">
                  {step === 'set' && 'This PIN protects your parent dashboard'}
                  {step === 'confirm' && 'Enter the same PIN to confirm'}
                  {step === 'done' && 'Your dashboard is now secured'}
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-8">
            {step === 'done' ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-[#1F2937] font-semibold mb-1">You're all set!</p>
                <p className="text-sm text-[#9CA3AF] mb-6">Your children won't be able to access parent settings without this PIN.</p>
                <Button
                  onClick={() => navigate('/family', { replace: true })}
                  className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-xl py-5 font-bold"
                  data-testid="continue-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {[0,1,2,3].map(i => (
                      <div key={i} className={`w-3 h-3 rounded-full transition-colors ${
                        step === 'set'
                          ? (pin[i] ? 'bg-[#C2410C]' : 'bg-gray-200')
                          : (confirmPin[i] ? 'bg-[#C2410C]' : 'bg-gray-200')
                      }`} />
                    ))}
                  </div>
                  <p className="text-sm text-[#4B5563]">
                    {step === 'set' ? 'Choose a 4 digit PIN' : 'Re-enter your PIN'}
                  </p>
                </div>

                <div className="flex justify-center gap-4 mb-6">
                  {[0, 1, 2, 3].map((i) => (
                    <input
                      key={i}
                      id={`${step === 'set' ? 'set' : 'confirm'}-pin-${i}`}
                      type={showPin ? 'text' : 'password'}
                      inputMode="numeric"
                      maxLength={1}
                      value={step === 'set' ? pin[i] : confirmPin[i]}
                      onChange={(e) => handlePinInput(i, e.target.value, step === 'confirm')}
                      onKeyDown={(e) => handleKeyDown(i, e, step === 'confirm')}
                      disabled={saving}
                      className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/20 focus:outline-none transition-all bg-[#FFFBF5] disabled:opacity-50"
                      data-testid={`pin-input-${i}`}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setShowPin(!showPin)}
                  className="flex items-center gap-1.5 mx-auto text-xs text-[#9CA3AF] hover:text-[#4B5563] transition-colors mb-6"
                >
                  {showPin ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {showPin ? 'Hide PIN' : 'Show PIN'}
                </button>

                <Button
                  onClick={step === 'set' ? handleSetPin : handleConfirm}
                  className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white rounded-xl py-5 font-bold disabled:opacity-40"
                  disabled={(step === 'set' ? pin : confirmPin).join('').length !== 4 || saving}
                  data-testid="pin-submit-btn"
                >
                  {saving ? 'Saving...' : step === 'set' ? 'Continue' : 'Confirm PIN'}
                  {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>

                {step === 'confirm' && (
                  <button
                    onClick={() => { setStep('set'); setPin(['','','','']); setConfirmPin(['','','','']); }}
                    className="w-full text-center text-sm text-[#9CA3AF] hover:text-[#C2410C] mt-3 transition-colors"
                  >
                    Start over
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-[#9CA3AF] mt-6">
          This PIN keeps your dashboard safe from curious little hands
        </p>
      </motion.div>
    </div>
  );
}
